import React, { useState, useEffect, useCallback } from 'react';
import type { MouseEvent } from 'react';
import { Typography } from '..';
import './accountAccordion.scss';

interface MenuItem {
  label: string;
  value?:string;
  onClick?: () => void;
}

interface AccordionProps {
  labelName?: string;
  menuItems?: MenuItem[];
  className?: string;
  theme?: 'light' | 'dark';
  isTitleStatic?: boolean;
  staticTitle?: string;
  selectedTitle?: string;
  onSelectedTitleChange?: (title: string) => void;
}

const Accordion: React.FC<AccordionProps> = ({
  labelName = '',
  menuItems = [],
  className = '',
  theme = 'light',
  isTitleStatic = false,
  staticTitle = '',
  selectedTitle,
  onSelectedTitleChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [internalSelectedTitle, setInternalSelectedTitle] = useState('');

  const isControlled = selectedTitle !== undefined;
  const displayedTitle = isControlled ? selectedTitle : internalSelectedTitle;

  useEffect(() => {
    if (!isControlled) {
      const defaultTitle =
        isTitleStatic && staticTitle
          ? staticTitle
          : !isTitleStatic && menuItems.length
          ? menuItems[0].label
          : '';
      setInternalSelectedTitle(defaultTitle);
    }
  }, [isControlled, isTitleStatic, staticTitle, menuItems]);

  const toggleAccordion = () => setIsOpen((prev) => !prev);

  const handleItemClick = useCallback(
    (e: MouseEvent<HTMLLIElement>, item: MenuItem) => {
      e.stopPropagation();

      if (!isTitleStatic) {
        if (isControlled) {
          onSelectedTitleChange?.(item.label);
        } else {
          setInternalSelectedTitle(item.label);
        }
      }

      item.onClick?.();
      setIsOpen(false);
    },
    [isControlled, isTitleStatic, onSelectedTitleChange]
  );

  return (
    <div className="accordion-wrapper">
      <div className={`accordion-container ${className}`} onClick={toggleAccordion}>
        <div className="label-title-container">
          <Typography variant="small-text-4" className="label">
            {labelName}
          </Typography>
          <Typography
            variant="subHeading-2"
            className={`title ${theme === 'dark' ? 'dark-text' : ''}`}
          >
            {displayedTitle || labelName}
          </Typography>
        </div>
        <div className={`arrow-icon ${theme === 'dark' ? 'dark-icon' : ''}`}>
          <i className={`bx ${isOpen ? 'bx-chevron-up' : 'bx-chevron-down'}`} />
        </div>
      </div>

      {isOpen && (
        <div className={`accordion-dropdown ${theme === 'light' ? 'dark-bg' : ''}`}>
          <ul>
            {menuItems.map((item, index) => (
              <li key={index} onClick={(e) => handleItemClick(e, item)}>
                <Typography variant="subHeading-4">{item.label}</Typography>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default Accordion;
